/**
 * Normalizes learning path API responses.
 * Handles wrappers (data, learningPlan, learning_path), snake_case,
 * and nested structures so the UI always receives camelCase LearningPlan.
 */

import type { LearningPlan, LearningModule } from '@/types/learningManagement';

type Raw = Record<string, unknown>;

function pick<T>(o: Raw, ...keys: string[]): T | undefined {
  for (const k of keys) {
    const v = o[k];
    if (v != null) return v as T;
  }
  return undefined;
}

function ensureArray<T>(x: unknown): T[] {
  if (Array.isArray(x)) return x as T[];
  if (x != null) return [x] as T[];
  return [];
}

function normalizeDay(raw: Raw): Record<string, unknown> {
  const day: Record<string, unknown> = {};
  const num = pick<number>(raw, 'dayNumber', 'day_number') ?? pick<number>(raw, 'day');
  if (num != null) day.dayNumber = num;
  const strKeys: [string, string[]][] = [
    ['scheduledDate', ['scheduledDate', 'scheduled_date']],
    ['title', ['title', 'lesson_title', 'lessonTitle', 'name', 'goal_title', 'goalTitle', 'label', 'heading', 'goal', 'day_goal', 'objective', 'outcome', 'focus', 'summary', 'content', 'text']],
    ['description', ['description', 'goalDescription', 'goal_description', 'day_description', 'dayDescription', 'lesson_description', 'lessonDescription', 'summary', 'content', 'objective', 'outcome', 'focus', 'goal', 'day_goal', 'text', 'name', 'title', 'lesson_title', 'lessonTitle']],
    ['priority', ['priority']],
  ];
  for (const [camel, aliases] of strKeys) {
    const v = pick<unknown>(raw, ...aliases);
    if (v != null && typeof v === 'string') day[camel] = v;
  }
  const arrKeys: [string, string[]][] = [
    ['learningObjectives', ['learningObjectives', 'learning_objectives', 'objectives']],
    ['lessonIds', ['lessonIds', 'lesson_ids']],
    ['nudgeIds', ['nudgeIds', 'nudge_ids']],
    ['tipIds', ['tipIds', 'tip_ids']],
    ['tidbitIds', ['tidbitIds', 'tidbit_ids']],
    ['hackIds', ['hackIds', 'hack_ids']],
    ['nanoIds', ['nanoIds', 'nano_ids']],
    ['reflectionIds', ['reflectionIds', 'reflection_ids']],
    ['reflectionPrompts', ['reflectionPrompts', 'reflection_prompts']],
    ['tags', ['tags']],
  ];
  for (const [camel, aliases] of arrKeys) {
    const v = pick<unknown>(raw, ...aliases);
    if (v != null) day[camel] = Array.isArray(v) ? v : [];
  }
  const aid = pick<string>(raw, 'assessmentId', 'assessment_id');
  if (aid != null) day.assessmentId = aid;
  const mins = pick<number>(raw, 'estimatedDurationMinutes', 'estimated_duration_minutes');
  if (mins != null) day.estimatedDurationMinutes = mins;
  const ms = pick<boolean>(raw, 'isMilestone', 'is_milestone');
  if (ms != null) day.isMilestone = ms;
  const statusRaw = pick<unknown>(raw, 'status', 'lesson_status', 'day_status', 'lessonStatus', 'dayStatus');
  if (statusRaw != null && typeof statusRaw === 'string') day.status = statusRaw;
  return day;
}

const SKILL_KEYS: [string, string[]][] = [
  ['skillId', ['skillId', 'skill_id']],
  ['skillName', ['skillName', 'skill_name', 'name']],
  ['proficiencyLevel', ['proficiencyLevel', 'proficiency_level']],
  ['description', ['description']],
];

function normalizeSkillSet(raw: Raw): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [camel, aliases] of SKILL_KEYS) {
    for (const k of aliases) {
      const v = raw[k];
      if (v != null) {
        out[camel] = v;
        break;
      }
    }
  }
  return Object.keys(out).length ? out : (raw as Record<string, unknown>);
}

function normalizeModule(raw: Raw): LearningModule {
  const mod = {} as LearningModule & { description?: string };
  const map: [string, string[]][] = [
    ['learningPathId', ['learningPathId', 'learning_path_id']],
    ['totalDays', ['totalDays', 'total_days']],
    ['finalAssessmentId', ['finalAssessmentId', 'final_assessment_id']],
    ['finalReflectionPrompts', ['finalReflectionPrompts', 'final_reflection_prompts']],
    ['description', ['description']],
  ];
  for (const [camel, aliases] of map) {
    const v = pick<unknown>(raw, ...aliases);
    if (v !== undefined) (mod as Record<string, unknown>)[camel] = v;
  }
  const dailyRaw = pick<unknown>(raw, 'dailyContents', 'daily_contents', 'dailyContent', 'goals');
  const daily = ensureArray<Raw>(dailyRaw).map(normalizeDay);
  if (daily.length) mod.dailyContents = daily as LearningModule['dailyContents'];
  return mod;
}

/**
 * Handle "learning paths by user" API shape:
 * - Root is an array: [ { id, userId, title, learningPathDetails: [ { userLearningPlan: { learningModules, ... } } ] }, ... ]
 * - Modules live in learningPathDetails[].userLearningPlan.learningModules
 */
function fromLearningPathDetailsArray(arr: unknown): LearningPlan | null {
  const items = ensureArray<Raw>(arr);
  if (items.length === 0) return null;
  const first = items[0] as Raw;
  const details = first.learningPathDetails ?? first.learning_path_details;
  const detailsArr = Array.isArray(details) ? details : [];
  const allModules: LearningModule[] = [];

  for (const d of detailsArr) {
    const detail = d as Raw;
    const plan = detail.userLearningPlan ?? detail.user_learning_plan;
    if (plan == null || typeof plan !== 'object') continue;
    const up = plan as Raw;
    const mods = up.learningModules ?? up.learning_modules ?? up.modules;
    const modArr = Array.isArray(mods) ? mods : [];
    for (const m of modArr) {
      allModules.push(normalizeModule(m as Raw));
    }
  }
  // Fallback: some APIs put dailyContents at plan level (learningPathDetails[0].userLearningPlan.dailyContents)
  if (allModules.length === 0 && detailsArr.length > 0) {
    const firstPlan = (detailsArr[0] as Raw).userLearningPlan ?? (detailsArr[0] as Raw).user_learning_plan;
    const up = firstPlan as Raw;
    if (up != null && typeof up === 'object') {
      const planDaily = pick<unknown>(up, 'dailyContents', 'daily_contents', 'dailyContent');
      const planDailyArr = ensureArray<Raw>(planDaily);
      if (planDailyArr.length > 0) {
        const syntheticMod = normalizeModule({ dailyContents: planDailyArr } as Raw);
        if (syntheticMod.dailyContents?.length) allModules.push(syntheticMod);
      }
    }
  }

  const title = typeof first.title === 'string' ? first.title : 'Learning Path';
  const statusRaw = first.status;
  const status = typeof statusRaw === 'string' ? statusRaw : 'NOT_STARTED';
  const addressingRaw = first.addressingSkillSets ?? first.addressing_skill_sets;
  let addressing = Array.isArray(addressingRaw)
    ? addressingRaw.map((s) => normalizeSkillSet((s as Raw) ?? {}))
    : [];
  if (addressing.length === 0) {
    const skillIdsRaw = first.addressing_skill_set_ids ?? first.skillIds ?? first.skill_ids;
    const idArr = Array.isArray(skillIdsRaw) ? skillIdsRaw : [];
    addressing = idArr
      .filter((x: unknown): x is string => typeof x === 'string' && x.length > 0)
      .map((id: string) => ({ skillId: id, skillName: id }));
  }
  let pain = first.painPoints ?? first.pain_points;
  if (!Array.isArray(pain) || pain.length === 0) {
    const painIds = first.pain_point_ids ?? first.painPointIds;
    pain = Array.isArray(painIds)
      ? painIds.filter((x: unknown): x is string => typeof x === 'string' && x.length > 0).map((id: string) => ({ painPointId: id, painPointDescription: id }))
      : [];
  }

  let estHours: number | undefined;
  for (const d of detailsArr) {
    const plan = (d as Raw).userLearningPlan ?? (d as Raw).user_learning_plan;
    if (plan != null && typeof plan === 'object') {
      const h = (plan as Raw).estimatedDurationHours ?? (plan as Raw).estimated_duration_hours;
      if (typeof h === 'number') {
        estHours = (estHours ?? 0) + h;
      }
    }
  }

  const winRaw = first.win;
  const winObj =
    winRaw && typeof winRaw === 'object' && !Array.isArray(winRaw)
      ? {
          winId: String((winRaw as Raw).winId ?? (winRaw as Raw).win_id ?? ''),
          winDescription: String((winRaw as Raw).winDescription ?? (winRaw as Raw).win_description ?? '').trim() || undefined,
        }
      : undefined;

  return {
    id: typeof first.id === 'string' ? first.id : undefined,
    title,
    description: typeof first.description === 'string' ? first.description : title,
    addressingSkillSets: addressing as unknown as LearningPlan['addressingSkillSets'],
    painPoints: Array.isArray(pain) ? pain : [],
    win: winObj?.winId || winObj?.winDescription ? winObj : undefined,
    status: (status === 'PLANNED' ? 'NOT_STARTED' : status) as LearningPlan['status'],
    difficultyLevel: 'INTERMEDIATE',
    learningModules: allModules,
    estimatedDurationHours: estHours,
  };
}

/** Unwrap plan from common API shapes and normalize to LearningPlan. */
export function normalizeLearningPathResponse(raw: unknown): LearningPlan {
  if (raw == null || typeof raw !== 'object') {
    throw new Error('Learning path API returned empty or invalid response');
  }

  // "Learning paths by user" API returns an array at root
  if (Array.isArray(raw)) {
    const fromArr = fromLearningPathDetailsArray(raw);
    if (fromArr != null) {
      return fromArr;
    }
    // Empty array: return minimal plan
    return {
      title: 'Learning Path',
      description: '',
      addressingSkillSets: [],
      painPoints: [],
      status: 'NOT_STARTED' as LearningPlan['status'],
      difficultyLevel: 'INTERMEDIATE',
      learningModules: [],
    };
  }

  const obj = raw as Raw;
  let plan = pick<Raw>(obj, 'data', 'learningPlan', 'learning_path', 'learningPath');
  if (!plan) plan = obj;

  const modulesRaw = pick<unknown>(plan, 'learningModules', 'learning_modules', 'modules');
  let modulesArr = ensureArray<Raw>(modulesRaw).map(normalizeModule);

  // Also check learningPathDetails[].userLearningPlan.learningModules
  if (modulesArr.length === 0) {
    const fromDetails = fromLearningPathDetailsArray([plan]);
    if (fromDetails?.learningModules?.length) {
      modulesArr = fromDetails.learningModules;
    }
  }

  const addressingRaw = plan.addressingSkillSets ?? plan.addressing_skill_sets;
  let addressing = Array.isArray(addressingRaw)
    ? addressingRaw.map((s) => normalizeSkillSet((s as Raw) ?? {}))
    : [];
  // Backend may return only skill IDs; build minimal skill sets so UI can resolve names (e.g. CoachJourneySummary)
  if (addressing.length === 0) {
    const skillIdsRaw = (plan as Raw).addressing_skill_set_ids ?? (plan as Raw).skillIds ?? (plan as Raw).skill_ids;
    const idArr = Array.isArray(skillIdsRaw) ? skillIdsRaw : [];
    addressing = idArr
      .filter((x: unknown): x is string => typeof x === 'string' && x.length > 0)
      .map((id: string) => ({ skillId: id, skillName: id }));
  }
  let pain = plan.painPoints ?? plan.pain_points;
  if (!Array.isArray(pain) || pain.length === 0) {
    const painIds = (plan as Raw).pain_point_ids ?? (plan as Raw).painPointIds;
    pain = Array.isArray(painIds)
      ? painIds.filter((x: unknown): x is string => typeof x === 'string' && x.length > 0).map((id: string) => ({ painPointId: id, painPointDescription: id }))
      : [];
  }

  const winRaw = plan.win ?? (plan as Raw).win;
  const winObj =
    winRaw && typeof winRaw === 'object' && !Array.isArray(winRaw)
      ? {
          winId: String((winRaw as Raw).winId ?? (winRaw as Raw).win_id ?? ''),
          winDescription: String((winRaw as Raw).winDescription ?? (winRaw as Raw).win_description ?? '').trim() || undefined,
        }
      : undefined;

  const out: LearningPlan = {
    ...(plan as Partial<LearningPlan>),
    title: (typeof plan.title === 'string' ? plan.title : 'Learning Path') as string,
    description: (typeof plan.description === 'string' ? plan.description : '') as string,
    addressingSkillSets: addressing as unknown as LearningPlan['addressingSkillSets'],
    painPoints: Array.isArray(pain) ? pain : [],
    win: winObj?.winId || winObj?.winDescription ? winObj : undefined,
    status: (typeof plan.status === 'string' ? plan.status : 'ACTIVE') as LearningPlan['status'],
    difficultyLevel: (typeof plan.difficultyLevel === 'string' ? plan.difficultyLevel : (typeof (plan as Raw).difficulty_level === 'string' ? (plan as Raw).difficulty_level : 'INTERMEDIATE')) as LearningPlan['difficultyLevel'],
    learningModules: modulesArr,
  };
  const est = plan.estimatedDurationHours ?? (plan as Raw).estimated_duration_hours;
  if (typeof est === 'number') out.estimatedDurationHours = est;
  return out;
}
